import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus } from 'lucide-react';
import { formatDelta, formatKpiValue, type KpiCard } from '../../../lib/reporting/socialOverview';
import { cn } from '../../../lib/utils';

// Organic Social Reporting — one hero KPI card. Three states, each visually
// distinct so a client never misreads them:
//   measured        — a real figure + period-over-period delta.
//   not-measurable  — the GBP/FB-story gap: an em dash + the "not measurable" note,
//                     NEVER a zero (zero engagement and no instrument differ).
//   pending         — sourced elsewhere (Community handled → the Community tab).
export function SocialKpiCard({ card }: { card: KpiCard }) {
  const delta = formatDelta(card);
  const measured = card.state === 'measured';

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{card.label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className={cn(
            'text-2xl font-semibold tabular-nums',
            measured ? 'text-slate-900' : 'text-slate-300',
          )}
        >
          {formatKpiValue(card)}
        </span>
        {measured && delta && <DeltaChip direction={card.direction} label={delta} />}
        {measured && !delta && card.previous !== null && (
          <span className="text-xs text-slate-400">no prior baseline</span>
        )}
      </div>
      {!measured && card.note && (
        <p className="mt-1 text-xs text-slate-400">{card.note}</p>
      )}
      {measured && (
        <p className="mt-1 text-xs text-slate-400">
          vs {formatPrevious(card)} last period
        </p>
      )}
    </div>
  );
}

function formatPrevious(card: KpiCard): string {
  if (card.previous === null) return '—';
  if (card.format === 'percent') return `${card.previous.toFixed(2)}%`;
  if (card.format === 'signed') return `${card.previous > 0 ? '+' : ''}${Math.round(card.previous).toLocaleString()}`;
  return Math.round(card.previous).toLocaleString();
}

function DeltaChip({
  direction,
  label,
}: {
  direction: KpiCard['direction'];
  label: string;
}) {
  const Icon =
    direction === 'up' ? ArrowUpRight : direction === 'down' ? ArrowDownRight : direction === 'flat' ? Minus : ArrowRight;
  const tone =
    direction === 'up'
      ? 'bg-emerald-50 text-emerald-700'
      : direction === 'down'
        ? 'bg-red-50 text-red-700'
        : 'bg-slate-100 text-slate-500';
  return (
    <span className={cn('inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium', tone)}>
      <Icon className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}
